<script setup lang="ts">
import { computed, useSlots } from 'vue';
import { classNames } from '@/js/lib/bem';

withDefaults(
  defineProps<{
    loading?: boolean
    error?: string | null
    empty?: boolean
    loadingMessage?: string
    emptyMessage?: string
  }>(),
  {
    loading: false,
    error: null,
    empty: false,
    loadingMessage: 'Loading…',
    emptyMessage: 'No items yet.',
  },
);

const cn = classNames('base-load-state');
const slots = useSlots();

const hasLoadingSlot = computed(() => Boolean(slots.loading));
const hasErrorSlot = computed(() => Boolean(slots.error));
const hasEmptySlot = computed(() => Boolean(slots.empty));
</script>

<template>
  <div :class="cn()">
    <template v-if="loading">
      <slot v-if="hasLoadingSlot" name="loading" />
      <p v-else :class="cn('message')" aria-live="polite">
        {{ loadingMessage }}
      </p>
    </template>

    <template v-else-if="error">
      <slot v-if="hasErrorSlot" name="error" :error="error" />
      <p v-else :class="cn('message', { error: true })" role="alert">
        {{ error }}
      </p>
    </template>

    <template v-else-if="empty">
      <slot v-if="hasEmptySlot" name="empty" />
      <p v-else :class="cn('message')" role="status">
        {{ emptyMessage }}
      </p>
    </template>

    <slot v-else />
  </div>
</template>

<style lang="scss" scoped>
.base-load-state {
  &__message {
    margin: 0;
    font-size: 16px;
    line-height: 1.5;
    color: var(--color-text-secondary);
    font-family: var(--font-family-sans);

    &_error {
      color: var(--color-text-accent);
    }
  }
}
</style>
